/**
 * Run-time brand (contract C2, C3.4, C4, A11). The organisation's name, colours and logos come from the guest
 * endpoint `onesphere.api.branding.public` (Branding Settings); anything unset falls back to the bundled OneSuite
 * defaults in `web/public/brand/`. Nothing here may throw or block the app: a slow, failing or malformed answer
 * leaves the last good brand (remembered in localStorage) or the defaults in place. Every colour and asset URL is
 * checked per field before use.
 */
import { z } from "zod";

const optionalText = z.string().nullable();

/** The C2.2 `message` body, key for key. */
export const BrandingSchema = z.object({
  branding_version: z.number(),
  organisation_name: z.string(),
  short_name: z.string(),
  logo_light_url: optionalText,
  logo_dark_url: optionalText,
  favicon_url: optionalText,
  login_background_url: optionalText,
  primary_colour: z.string(),
  accent_colour: z.string(),
  deep_colour: z.string(),
  address: optionalText,
  support_email: optionalText,
  support_phone: optionalText,
  legal_footer: optionalText,
  deep_link_base: z.string(),
  portal_url: optionalText,
  // C2.2: clients must not need `defaults`; without it, `isDefault` compares against the default values (A3)
  defaults: z.array(z.string()).optional(),
});
export type Branding = z.infer<typeof BrandingSchema>;

export const BRANDING_URL = "/api/method/onesphere.api.branding.public";

/** The C2.2 neutral default. `deep_link_base` is empty: only the server knows its public URL. */
export const BRAND_DEFAULTS: Branding = {
  branding_version: 0,
  organisation_name: "OneSuite",
  short_name: "OneSuite",
  logo_light_url: null,
  logo_dark_url: null,
  favicon_url: null,
  login_background_url: null,
  primary_colour: "#2B5672",
  accent_colour: "#4EB7F5",
  deep_colour: "#005197",
  address: null,
  support_email: null,
  support_phone: null,
  legal_footer: null,
  deep_link_base: "",
  portal_url: null,
  defaults: ["organisation_name", "short_name", "logo_light", "logo_dark", "favicon", "login_background",
             "primary_colour", "accent_colour", "address", "support_email", "support_phone",
             "legal_footer", "deep_link_base", "portal_url"],
};

export type BrandVar =
  | "--brand-primary" | "--brand-accent" | "--brand-deep" | "--brand-charcoal" | "--brand-grey" | "--brand-grey-light"
  | "--brand-red" | "--brand-green" | "--brand-deep-2" | "--brand-red-hover";

/** Default value of every brand CSS variable (C3.2, C3.7). Only the first three change at run time. */
export const CSS_DEFAULTS: Record<BrandVar, string> = {
  "--brand-primary": "#2B5672",
  "--brand-accent": "#4EB7F5",
  "--brand-deep": "#005197",
  "--brand-charcoal": "#464646",
  "--brand-grey": "#787878",
  "--brand-grey-light": "#E3E3E3",
  "--brand-red": "#DA251D",
  "--brand-green": "#8CC63F",
  "--brand-deep-2": "#0B2A44",
  "--brand-red-hover": "#C11F18",
};

/** The bundled OneSuite files (C4.2), used whenever the payload's URL is null. */
const BUNDLED_LOGOS = {
  light: "/brand/onesuite-wordmark-light.svg",
  dark: "/brand/onesuite-wordmark-dark.svg",
  mark: "/brand/onesuite-mark.svg",
} as const;

const HEX = /^#[0-9A-Fa-f]{6}$/;

/** A colour from the payload when it is a plain `#RRGGBB`, else the default. Nothing else ever reaches
 * `setProperty` or the theme-color meta. */
function safeColour(value: unknown, fallback: string): string {
  return typeof value === "string" && HEX.test(value) ? value : fallback;
}

/** `scheme://host…` spelled out in full: a relative path, `//host` and a lenient `https:/host` all fail it. */
const ABSOLUTE_HTTP = /^https?:\/\/[^/\\\s]/i;

/** An asset URL that may go into `<img src>` or the favicon href: an absolute http(s) URL only. `public()` always
 * sends absolute asset URLs (C2.3: `{onesphere_public_url}{file_url}?v=…`) and no caller passes a relative one,
 * so a relative or protocol-relative (`//host`) value is refused like `javascript:`, `data:` or junk: null, which
 * means "use the bundled file". The Keycloak theme script and tools/brand_source.py also accept absolute http(s)
 * only. */
export function safeAssetUrl(u: string | null | undefined): string | null {
  if (!u || !ABSOLUTE_HTTP.test(u)) return null;
  try {
    const { protocol } = new URL(u);
    return protocol === "http:" || protocol === "https:" ? u : null;
  } catch {
    return null;
  }
}

/** Per-field checks on a parsed brand: a bad colour falls back to its default, a bad asset URL to null. */
export function sanitizeBranding(b: Branding): Branding {
  return {
    ...b,
    primary_colour: safeColour(b.primary_colour, CSS_DEFAULTS["--brand-primary"]),
    accent_colour: safeColour(b.accent_colour, CSS_DEFAULTS["--brand-accent"]),
    deep_colour: safeColour(b.deep_colour, CSS_DEFAULTS["--brand-deep"]),
    logo_light_url: safeAssetUrl(b.logo_light_url),
    logo_dark_url: safeAssetUrl(b.logo_dark_url),
    favicon_url: safeAssetUrl(b.favicon_url),
    login_background_url: safeAssetUrl(b.login_background_url),
  };
}

/** Whether a field fell back to its default: from `defaults` when the server sent it, else by comparing the value
 * with the default value (A3). */
export function isDefault(b: Branding, field: "organisation_name"): boolean {
  return b.defaults ? b.defaults.includes(field) : b[field] === BRAND_DEFAULTS[field];
}

/** Where the last good brand is remembered, so a configured tenant starts with its brand (offline too). */
export const BRAND_STORAGE_KEY = "onesphere:branding";
export type BrandStorage = Pick<Storage, "getItem" | "setItem">;

function browserStorage(): BrandStorage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

/** The remembered brand, checked exactly like a fresh payload; null when there is none or it does not parse. */
export function loadStoredBranding(storage: BrandStorage | null = browserStorage()): Branding | null {
  try {
    const raw = storage?.getItem(BRAND_STORAGE_KEY);
    if (!raw) return null;
    const parsed = BrandingSchema.safeParse(JSON.parse(raw));
    return parsed.success ? sanitizeBranding(parsed.data) : null;
  } catch {
    return null;
  }
}

function storeBranding(b: Branding, storage: BrandStorage | null): void {
  try {
    storage?.setItem(BRAND_STORAGE_KEY, JSON.stringify(b));
  } catch {
    // private mode, quota, blocked storage: the brand still applies, it is just not remembered
  }
}

/** GET the brand and remember it. On a timeout, a network error, a non-200 or a body that fails the schema it
 * resolves the last good brand, else `BRAND_DEFAULTS`; it never rejects. The result is always sanitised. */
export async function fetchBranding(fetchFn: typeof fetch = fetch, timeoutMs = 3000,
                                    storage: BrandStorage | null = browserStorage()): Promise<Branding> {
  const fallback = () => loadStoredBranding(storage) ?? BRAND_DEFAULTS;
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<Branding>((resolve) => {
    timer = setTimeout(() => { controller.abort(); resolve(fallback()); }, timeoutMs);
  });
  const load = (async (): Promise<Branding> => {
    try {
      // No manual If-None-Match and no `cache: "no-cache"`: the browser's HTTP cache revalidates with the ETag by
      // itself and turns a 304 into the cached 200, so a 304 never reaches this code. Sending the header by hand
      // would hand a bodiless 304 to JS (read here as a failure) and defeat that cache.
      const res = await fetchFn(BRANDING_URL, { signal: controller.signal, headers: { Accept: "application/json" } });
      if (res.status !== 200) return fallback();
      const parsed = BrandingSchema.safeParse(((await res.json()) as { message?: unknown } | null)?.message);
      if (!parsed.success) return fallback();
      const b = sanitizeBranding(parsed.data);
      storeBranding(b, storage);
      return b;
    } catch {
      return fallback();
    }
  })();
  try {
    return await Promise.race([load, timeout]);
  } finally {
    clearTimeout(timer);
  }
}

let initialBrand: Branding | undefined;
/** The brand to show before the fetch answers: the remembered one, else the defaults (computed once, so the
 * placeholder keeps one identity across renders). */
export function initialBranding(): Branding {
  return (initialBrand ??= loadStoredBranding() ?? BRAND_DEFAULTS);
}

/** The slice of `document` that `applyBranding` writes to (a plain object in tests). */
export interface BrandDocument {
  documentElement: { style: { setProperty(k: string, v: string): void } };
  title: string;
  querySelector(sel: string): { setAttribute(k: string, v: string): void } | null;
}

/** C3.4: the three settable colours, the title (C12), the favicon and the theme colour. Every value is checked
 * again here, whatever the caller passes. */
export function applyBranding(b: Branding, doc: BrandDocument = document): void {
  const primary = safeColour(b.primary_colour, CSS_DEFAULTS["--brand-primary"]);
  const style = doc.documentElement.style;
  style.setProperty("--brand-primary", primary);
  style.setProperty("--brand-accent", safeColour(b.accent_colour, CSS_DEFAULTS["--brand-accent"]));
  style.setProperty("--brand-deep", safeColour(b.deep_colour, CSS_DEFAULTS["--brand-deep"]));
  doc.title = isDefault(b, "organisation_name") ? "OneSphere" : `${b.organisation_name} · OneSphere`;
  doc.querySelector('link[rel="icon"]')?.setAttribute("href", logoUrl(b, "mark"));
  doc.querySelector('meta[name="theme-color"]')?.setAttribute("content", primary);
}

/** A brand colour for code that cannot use a CSS class (charts, maps): the live CSS variable, else its default. */
export function brandColour(name: BrandVar, doc?: Pick<Document, "documentElement">): string {
  try {
    if (typeof window === "undefined" || typeof getComputedStyle !== "function") return CSS_DEFAULTS[name];
    const value = getComputedStyle((doc ?? document).documentElement).getPropertyValue(name).trim();
    return value || CSS_DEFAULTS[name];
  } catch {
    return CSS_DEFAULTS[name];
  }
}

/** The organisation's logo for a surface (checked by `safeAssetUrl`), or the bundled OneSuite file. `mark` is the
 * square icon (the favicon). */
export function logoUrl(b: Branding, variant: "light" | "dark" | "mark"): string {
  const fromPayload = variant === "light" ? b.logo_light_url : variant === "dark" ? b.logo_dark_url : b.favicon_url;
  return safeAssetUrl(fromPayload) ?? BUNDLED_LOGOS[variant];
}

/** A11: the footer line that replaced the old motto: the legal footer, else the organisation name when it was set;
 * with the settings empty, nothing. */
export function footerLine(b: Branding): string | null {
  if (b.legal_footer) return b.legal_footer;
  if (!isDefault(b, "organisation_name")) return b.organisation_name;
  return null;
}
